import { useCallback, useState } from 'react'
import type { ContentSectionKey } from '@/content'
import { CONTENT_SECTIONS, useContent } from '@/content'
import { cn } from '@/lib/cn'
import { ROUTES } from '@/lib/paths'
import { Icon } from '@/components/ui'
import { Logo } from '@/components/layout'
import { SectionEditor } from './SectionEditor'
import styles from './AdminDashboard.module.css'

interface AdminDashboardProps {
  token: string
  email: string | null
  onSignOut: () => void
}

/**
 * The content admin.
 *
 * A list of sections on the left, a form for the chosen one on the right.
 * Each section owns its own draft and its own save button, so editing the
 * projects cannot disturb the contact details, and a save that fails leaves
 * everything else exactly as it was.
 *
 * The forms are generated from the shape of the content rather than
 * hand-written per section — see `inferFields`. That keeps the panel in step
 * with the site: a field added to `src/data` becomes editable as soon as it
 * ships, with no second place to remember to update.
 */
export const AdminDashboard = ({
  token,
  email,
  onSignOut,
}: AdminDashboardProps) => {
  const content = useContent()
  const [active, setActive] = useState<ContentSectionKey>('site')
  const [dirty, setDirty] = useState(false)

  const section = CONTENT_SECTIONS.find((item) => item.key === active)

  const choose = (key: ContentSectionKey) => {
    if (key === active) return
    // The draft lives inside the editor and is lost on switch, so say so
    // rather than discarding work silently.
    if (
      dirty &&
      !window.confirm('You have unsaved changes here. Leave them behind?')
    ) {
      return
    }
    setDirty(false)
    setActive(key)
  }

  // Stable, so the editor's effect does not re-run on every render.
  const onDirtyChange = useCallback((next: boolean) => setDirty(next), [])

  return (
    <div className={styles.shell}>
      <aside className={styles.sidebar}>
        <div className={styles.brand}>
          <Logo size={26} />
          <a href={ROUTES.home} className={styles.viewSite}>
            View site
            <Icon name="arrowUpRight" size={12} />
          </a>
        </div>

        <nav aria-label="Content sections">
          <ul className={styles.sections}>
            {CONTENT_SECTIONS.map((item) => (
              <li key={item.key}>
                <button
                  type="button"
                  onClick={() => choose(item.key)}
                  className={cn(
                    styles.section,
                    active === item.key && styles.sectionActive,
                  )}
                >
                  <span>{item.label}</span>
                  {active === item.key && dirty && (
                    <span className={styles.dot} aria-label="unsaved changes" />
                  )}
                </button>
              </li>
            ))}
          </ul>
        </nav>

        <div className={styles.account}>
          {email && <p className={styles.email}>{email}</p>}
          <button type="button" onClick={onSignOut} className={styles.ghostButton}>
            Sign out
          </button>
        </div>
      </aside>

      <main className={styles.main}>
        {section && (
          <SectionEditor
            // Keyed so switching sections mounts a fresh editor with a fresh
            // draft, rather than carrying one section's edits into another.
            key={section.key}
            sectionKey={section.key}
            label={section.label}
            live={(content as unknown as Record<string, unknown>)[section.key]}
            token={token}
            onDirtyChange={onDirtyChange}
          />
        )}
      </main>
    </div>
  )
}
